
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyMembership } from '@/lib/supabase/membership/verifyMembership';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';

const AdhesionVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [membershipId, setMembershipId] = useState<string | null>(null);

  useEffect(() => {
    console.log("🔍 AdhesionVerification.tsx: Component mounted with URL params:", {
      id: searchParams.get('id'),
      token: searchParams.get('token'),
      fullUrl: window.location.href,
      pathname: window.location.pathname,
      hash: window.location.hash,
      search: window.location.search,
      origin: window.location.origin,
      component: "AdhesionVerification.tsx",
      timestamp: new Date().toISOString()
    });

    const id = searchParams.get('id');
    const token = searchParams.get('token');

    const verifyToken = async () => {
      if (!id || !token) {
        console.error("⚠️ Missing parameters:", { id, token });
        setError('Paramètres manquants dans l\'URL. Veuillez vérifier le lien que vous avez reçu.');
        setIsVerifying(false);
        return;
      }

      try {
        console.log("🔄 Starting verification with:", { id, token });
        const result = await verifyMembership(id, token);
        
        console.log("✅ Verification successful:", result);
        setIsVerified(true);
        setMembershipId(id);
        
        // Show success toast
        toast({
          title: "Adhésion vérifiée",
          description: result.message || "Adhésion vérifiée avec succès"
        });
        
        console.log("🔄 Preparing to redirect to payment page with ID:", id);
        
        // Fix: Ensure proper redirect to payment page
        const paymentUrl = `/adhesion-paiement?id=${id}&token=${token}`;
        console.log("🔄 Redirecting to:", paymentUrl, {
          navigateMethod: "useNavigate hook",
          currentPathname: window.location.pathname
        });
        
        // Use setTimeout to ensure all state updates and toasts are processed
        setTimeout(() => {
          // Use React Router for navigation
          navigate(paymentUrl, { replace: true });
          console.log("💡 Redirecting with navigate():", paymentUrl);
        }, 1500);
        
      } catch (err: any) {
        console.error("❌ Verification error:", err);
        setError(err.message || "Une erreur est survenue lors de la vérification");
        
        // Show error toast
        toast({
          variant: "destructive",
          title: "Erreur de vérification",
          description: err.message || "Une erreur est survenue"
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  // Backup function for manual redirection to payment page
  const redirectToPayment = () => {
    if (membershipId) {
      const paymentUrl = `/adhesion-paiement?id=${membershipId}&token=${searchParams.get('token')}`;
      console.log("🔄 Manual redirect to payment page:", {
        paymentUrl,
        membershipId,
        navigateMethod: "useNavigate hook"
      });
      
      // Use React Router for navigation
      navigate(paymentUrl, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24">
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
            <h1 className="text-2xl font-bold text-center mb-6">Vérification de votre adhésion</h1>
            
            {isVerifying ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-center text-body">Vérification en cours...</p>
              </div>
            ) : error ? (
              <div className="text-center py-4">
                <div className="text-destructive mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
                <p className="text-body">{error}</p>
                <div className="mt-6">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      console.log("🔄 Returning to adhesion page");
                      navigate('/adhesion');
                    }}
                  >
                    Retour à la page d'adhésion
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-green-500 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold mb-2">Vérification réussie</h2>
                <p className="text-body mb-4">Votre adhésion a été vérifiée avec succès. Vous allez être redirigé vers la page de paiement...</p>
                <Button onClick={redirectToPayment} className="w-full">
                  Procéder au paiement
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdhesionVerification;
